/**
 * Buổi khám (ClinicSession) client — các khung buổi trong ngày của phòng khám
 * (VD: Sáng / Chiều / Tối), dùng cho luồng đặt lịch linh hoạt (P2/P3).
 * Real /api/v1/clinic-sessions — real-only (mirror services.ts / bookingPolicy.ts),
 * không dùng mock adapter.
 */
import { apiClient } from "./client";

export interface ClinicSession {
  id: string;
  branchId: string | null;
  code: string;
  label: string;
  /** "HH:mm" */
  startTime: string;
  /** "HH:mm" */
  endTime: string;
  /** null = không giới hạn. */
  capacity: number | null;
  /** #19953 — không còn quyết định thứ tự (danh sách theo giờ bắt đầu); chỉ còn trên API. */
  sortOrder: number;
  active: boolean;
  /**
   * #19935 — "Buổi ngoài giờ (khám dịch vụ)": được đặt lịch cả khi nằm trong khoảng trống giữa các
   * buổi thường (và, ở #19938, ngoài Giờ làm việc). Vắng trên API cũ.
   */
  afterHours?: boolean;
}

export interface ClinicSessionCreateInput {
  code: string;
  label: string;
  startTime: string;
  endTime: string;
  capacity?: number | null;
  /** #19935 — Buổi ngoài giờ. */
  afterHours?: boolean;
}

export interface ClinicSessionUpdateInput {
  label: string;
  startTime: string;
  endTime: string;
  capacity?: number | null;
  /** #19935 — Buổi ngoài giờ. */
  afterHours?: boolean;
}

/** Gợi ý buổi mặc định để quick-add khi danh sách còn trống. */
export const DEFAULT_SESSION_SUGGESTIONS: {
  code: string;
  label: string;
  startTime: string;
  endTime: string;
}[] = [
  { code: "MORNING", label: "Sáng", startTime: "07:30", endTime: "11:30" },
  { code: "AFTERNOON", label: "Chiều", startTime: "13:30", endTime: "17:00" },
  { code: "EVENING", label: "Tối", startTime: "18:00", endTime: "20:30" },
];

/** Tầng đang quyết định khung giờ đặt lịch. */
export type SessionTier = "BRANCH" | "TENANT_DEFAULT" | "NONE";

export interface EffectiveSessions {
  tier: SessionTier;
  branchId: string | null;
  sessions: ClinicSession[];
}

export const clinicSessionsApi = {
  list(): Promise<ClinicSession[]> {
    return apiClient
      .get<ClinicSession[]>("/v1/clinic-sessions")
      .then((r) => r.data);
  },
  /**
   * Bộ buổi khám engine đặt lịch THẬT SỰ đọc, kèm tầng (#19688 mục 3).
   *
   * `list()` không kèm branchId chỉ trả bản ghi mức phòng khám, còn engine ưu tiên bản ghi của
   * chi nhánh — nên màn hình cần một câu trả lời riêng cho "cấp nào đang thắng", và nó phải đến
   * từ server vì FE không biết chi nhánh nào đang được dùng.
   */
  effective(): Promise<EffectiveSessions> {
    return apiClient
      .get<EffectiveSessions>("/v1/clinic-sessions/effective")
      .then((r) => r.data);
  },
  create(req: ClinicSessionCreateInput): Promise<ClinicSession> {
    return apiClient
      .post<ClinicSession>("/v1/clinic-sessions", req)
      .then((r) => r.data);
  },
  update(id: string, req: ClinicSessionUpdateInput): Promise<ClinicSession> {
    return apiClient
      .put<ClinicSession>(`/v1/clinic-sessions/${id}`, req)
      .then((r) => r.data);
  },
  disable(id: string): Promise<ClinicSession> {
    return apiClient
      .post<ClinicSession>(`/v1/clinic-sessions/${id}/disable`)
      .then((r) => r.data);
  },
  enable(id: string): Promise<ClinicSession> {
    return apiClient
      .post<ClinicSession>(`/v1/clinic-sessions/${id}/enable`)
      .then((r) => r.data);
  },
  /** Hard delete (permanent). 409 nếu buổi khám đang được tham chiếu. */
  remove(id: string): Promise<void> {
    return apiClient.delete(`/v1/clinic-sessions/${id}/permanent`).then(() => undefined);
  },
};

export const clinicSessionsQueryKeys = {
  all: () => ["clinic-sessions"] as const,
  effective: () => ["clinic-sessions", "effective"] as const,
};
